import React, { useState, useMemo } from 'react';
import {
  ArrowLeft,
  Grid,
  List,
  TrendingUp,
  Star,
  Trophy,
  Sparkles,
  Package,
  Clock,
  Search,
} from 'lucide-react';
import {
  BandEntry,
  BandSortOption,
  BandFilterOption,
  extractBandsFromSmokeLog,
  sortBands,
  filterBands,
  getBandStats,
  ICONIC_BANDS,
} from '../data/bands';
import { getSmokeLog } from '../data/personalization';

interface BandGalleryViewProps {
  onBack: () => void;
}

export const BandGalleryView: React.FC<BandGalleryViewProps> = ({ onBack }) => {
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [sortBy, setSortBy] = useState<BandSortOption>('recent');
  const [filterBy, setFilterBy] = useState<BandFilterOption>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedBand, setSelectedBand] = useState<BandEntry | null>(null);

  // Get smoke log entries and extract bands
  const smokeLog = useMemo(() => getSmokeLog(), []);
  const allBands = useMemo(() => extractBandsFromSmokeLog(smokeLog), [smokeLog]);
  const stats = useMemo(() => getBandStats(allBands), [allBands]);

  // Filter and sort bands
  const displayBands = useMemo(() => {
    let bands = filterBands(allBands, filterBy);

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      bands = bands.filter(
        b =>
          b.brand.toLowerCase().includes(query) ||
          b.line?.toLowerCase().includes(query)
      );
    }

    return sortBands(bands, sortBy);
  }, [allBands, filterBy, sortBy, searchQuery]);

  // Stats card component
  const StatCard = ({
    icon: Icon,
    label,
    value,
    color = 'amber',
  }: {
    icon: React.ElementType;
    label: string;
    value: string | number;
    color?: string;
  }) => (
    <div className="bg-stone-800/50 rounded-xl p-3 text-center">
      <Icon className={`w-5 h-5 mx-auto mb-1 text-${color}-500`} />
      <div className="text-lg font-bold text-white">{value}</div>
      <div className="text-xs text-stone-400">{label}</div>
    </div>
  );

  // Band card component
  const BandCard = ({ band }: { band: BandEntry }) => {
    const isGridView = viewMode === 'grid';

    if (isGridView) {
      return (
        <button
          onClick={() => setSelectedBand(band)}
          className="relative aspect-square rounded-xl overflow-hidden group"
        >
          <img
            src={band.imageUrl}
            alt={`${band.brand} band`}
            className="w-full h-full object-cover transition-transform group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-3">
            <h3 className="text-sm font-medium text-white truncate">{band.brand}</h3>
            {band.line && (
              <p className="text-xs text-stone-300 truncate">{band.line}</p>
            )}
            <div className="flex items-center gap-2 mt-1">
              {band.avgRating && (
                <span className="flex items-center gap-0.5 text-xs text-amber-400">
                  <Star className="w-3 h-3 fill-amber-400" />
                  {band.avgRating.toFixed(1)}
                </span>
              )}
              <span className="text-xs text-stone-400">×{band.userLogCount}</span>
            </div>
          </div>
          {band.logCount <= 3 && (
            <div className="absolute top-2 right-2 px-1.5 py-0.5 bg-purple-500/80 rounded text-[10px] font-medium text-white">
              Rare
            </div>
          )}
        </button>
      );
    }

    return (
      <button
        onClick={() => setSelectedBand(band)}
        className="w-full flex items-center gap-4 p-3 bg-stone-800/50 rounded-xl hover:bg-stone-800 transition-colors"
      >
        <img
          src={band.imageUrl}
          alt={`${band.brand} band`}
          className="w-16 h-16 object-cover rounded-lg"
        />
        <div className="flex-1 text-left">
          <h3 className="font-medium text-white">{band.brand}</h3>
          {band.line && <p className="text-sm text-stone-400">{band.line}</p>}
          <div className="flex items-center gap-3 mt-1 text-xs text-stone-500">
            {band.avgRating && (
              <span className="flex items-center gap-1 text-amber-400">
                <Star className="w-3 h-3 fill-amber-400" />
                {band.avgRating.toFixed(1)}
              </span>
            )}
            <span>Logged {band.userLogCount}×</span>
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {new Date(band.lastLogged).toLocaleDateString()}
            </span>
          </div>
        </div>
        {band.logCount <= 3 && (
          <div className="px-2 py-1 bg-purple-500/20 rounded text-xs font-medium text-purple-400">
            Rare
          </div>
        )}
      </button>
    );
  };

  // Band detail modal
  const BandDetailModal = ({ band }: { band: BandEntry }) => (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
      <div className="bg-stone-900 rounded-2xl w-full max-w-md overflow-hidden border border-stone-700">
        <div className="relative h-48">
          <img
            src={band.imageUrl}
            alt={`${band.brand} band`}
            className="w-full h-full object-cover"
          />
          <button
            onClick={() => setSelectedBand(null)}
            className="absolute top-4 right-4 p-2 bg-black/50 rounded-full text-white hover:bg-black/70"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6">
          <h2 className="text-xl font-bold text-white">{band.brand}</h2>
          {band.line && <p className="text-stone-400">{band.line}</p>}

          <div className="grid grid-cols-3 gap-3 mt-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-amber-500">
                {band.avgRating?.toFixed(1) || '-'}
              </div>
              <div className="text-xs text-stone-400">Avg Rating</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-amber-500">
                {band.userLogCount}
              </div>
              <div className="text-xs text-stone-400">Times Logged</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-amber-500">
                {band.allImageUrls.length}
              </div>
              <div className="text-xs text-stone-400">Photos</div>
            </div>
          </div>

          <div className="mt-4 space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-stone-400">First Logged</span>
              <span className="text-stone-200">
                {new Date(band.firstLogged).toLocaleDateString()}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-stone-400">Last Logged</span>
              <span className="text-stone-200">
                {new Date(band.lastLogged).toLocaleDateString()}
              </span>
            </div>
          </div>

          {band.allImageUrls.length > 1 && (
            <div className="mt-4">
              <h3 className="text-sm font-medium text-stone-400 mb-2">
                All Photos ({band.allImageUrls.length})
              </h3>
              <div className="flex gap-2 overflow-x-auto pb-2">
                {band.allImageUrls.map((url, i) => (
                  <img
                    key={i}
                    src={url}
                    alt=""
                    className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
                  />
                ))}
              </div>
            </div>
          )}

          <button
            onClick={() => setSelectedBand(null)}
            className="w-full mt-4 py-3 bg-stone-800 hover:bg-stone-700 rounded-lg font-medium transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );

  // Empty state for when user has no bands
  const EmptyState = () => (
    <div className="text-center py-12 px-4">
      <Package className="w-16 h-16 mx-auto text-stone-600 mb-4" />
      <h3 className="text-lg font-semibold text-white mb-2">
        Start Your Band Collection
      </h3>
      <p className="text-stone-400 mb-6 max-w-xs mx-auto">
        Capture cigar bands when you log your smokes to build your personal gallery.
      </p>

      <div className="mt-8">
        <h4 className="text-sm font-medium text-stone-400 mb-4 flex items-center justify-center gap-2">
          <Sparkles className="w-4 h-4 text-amber-500" />
          Iconic Bands to Discover
        </h4>
        <div className="space-y-3 max-w-sm mx-auto">
          {ICONIC_BANDS.slice(0, 5).map((band, i) => (
            <div
              key={i}
              className="p-3 bg-stone-800/50 rounded-lg text-left"
            >
              <h5 className="font-medium text-white">
                {band.brand} - {band.line}
              </h5>
              <p className="text-xs text-stone-400 mt-1">{band.description}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-stone-900 text-white">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-stone-900/95 backdrop-blur-sm border-b border-stone-800">
        <div className="px-4 py-4">
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              className="p-2 hover:bg-stone-800 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold">Band Gallery</h1>
              <p className="text-xs text-stone-400">
                {stats.myBandCount} band{stats.myBandCount !== 1 ? 's' : ''} collected
              </p>
            </div>
          </div>
        </div>

        {/* Stats */}
        {allBands.length > 0 && (
          <div className="px-4 pb-4">
            <div className="grid grid-cols-4 gap-2">
              <StatCard icon={Package} label="Bands" value={stats.myBandCount} />
              <StatCard icon={Star} label="Avg" value={stats.averageRating || '-'} />
              <StatCard icon={TrendingUp} label="Top" value={stats.topBrand || '-'} color="green" />
              <StatCard icon={Trophy} label="Rare" value={stats.rareBandCount} color="purple" />
            </div>
          </div>
        )}

        {/* Search and Filters */}
        {allBands.length > 0 && (
          <div className="px-4 pb-3 space-y-3">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-500" />
              <input
                type="text"
                placeholder="Search bands..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-stone-800 border border-stone-700 rounded-lg text-white placeholder-stone-500 focus:outline-none focus:border-amber-500"
              />
            </div>

            {/* Filter Pills */}
            <div className="flex gap-2 overflow-x-auto pb-1">
              {(
                [
                  { value: 'all', label: 'All' },
                  { value: 'my-collection', label: 'My Collection' },
                  { value: 'trending', label: 'Trending' },
                  { value: 'rare', label: 'Rare Finds' },
                ] as const
              ).map(filter => (
                <button
                  key={filter.value}
                  onClick={() => setFilterBy(filter.value)}
                  className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors ${
                    filterBy === filter.value
                      ? 'bg-amber-500 text-white'
                      : 'bg-stone-800 text-stone-300 hover:bg-stone-700'
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>

            {/* Sort and View Toggle */}
            <div className="flex items-center justify-between">
              <select
                value={sortBy}
                onChange={e => setSortBy(e.target.value as BandSortOption)}
                className="px-3 py-1.5 bg-stone-800 border border-stone-700 rounded-lg text-sm text-white focus:outline-none focus:border-amber-500"
              >
                <option value="recent">Most Recent</option>
                <option value="popular">Most Logged</option>
                <option value="rating">Highest Rated</option>
                <option value="brand">By Brand</option>
                <option value="rare">Rarest</option>
              </select>

              <div className="flex bg-stone-800 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-1.5 rounded transition-colors ${
                    viewMode === 'grid' ? 'bg-amber-500 text-white' : 'text-stone-400'
                  }`}
                >
                  <Grid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-1.5 rounded transition-colors ${
                    viewMode === 'list' ? 'bg-amber-500 text-white' : 'text-stone-400'
                  }`}
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        {displayBands.length === 0 ? (
          <EmptyState />
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {displayBands.map(band => (
              <BandCard key={band.id} band={band} />
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            {displayBands.map(band => (
              <BandCard key={band.id} band={band} />
            ))}
          </div>
        )}

        {/* Iconic Bands Discovery (shown at bottom when user has some bands) */}
        {allBands.length > 0 && allBands.length < 10 && (
          <div className="mt-8 pt-8 border-t border-stone-800">
            <h3 className="text-sm font-medium text-stone-400 mb-4 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-amber-500" />
              Iconic Bands to Hunt
            </h3>
            <div className="grid grid-cols-2 gap-3">
              {ICONIC_BANDS.filter(
                iconic =>
                  !allBands.some(
                    b =>
                      b.brand.toLowerCase() === iconic.brand.toLowerCase() &&
                      b.line?.toLowerCase() === iconic.line.toLowerCase()
                  )
              )
                .slice(0, 4)
                .map((band, i) => (
                  <div
                    key={i}
                    className="p-3 bg-stone-800/50 rounded-lg border border-stone-700/50"
                  >
                    <h4 className="font-medium text-white text-sm">{band.brand}</h4>
                    <p className="text-xs text-amber-500">{band.line}</p>
                    <p className="text-xs text-stone-500 mt-1 line-clamp-2">
                      {band.description}
                    </p>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>

      {/* Band Detail Modal */}
      {selectedBand && <BandDetailModal band={selectedBand} />}
    </div>
  );
};

export default BandGalleryView;
